import LoadingAnimationIcon from '@/components/icons/loading-animation-icon';
import CardGiftProduct from '@/components/sections/builder/elements/header/header/cart-dropdown/card-gift-product';
import CardGiftProductItem from '@/components/sections/builder/elements/header/header/cart-dropdown/card-gift-product-item';
import { Button } from '@/components/ui/button';
import { DrawerClose, DrawerFooter, DrawerHeader, DrawerTitle } from '@/components/ui/drawer';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Skeleton } from '@/components/ui/skeleton';
import {
  useGetRewardProductInfinite,
  useGetRewardProductItemInfinite,
} from '@/hooks/api/storefront/promotion';
import { useDebounceValue } from '@/hooks/use-debounce';
import { ThemeColorSchemeType } from '@/stores/builder';
import { BaseFreeGift } from '@/stores/promotion';
import {
  BasePromotion,
  BasePromotionRewardProduct,
  BasePromotionRewardProductItem,
} from '@/types/http/promotion/common';
import { ArrowLeft, XIcon } from 'lucide-react';
import { ChangeEvent, useCallback, useEffect, useState } from 'react';
import { useInView } from 'react-intersection-observer';

type FreeGiftListProps = {
  promotion: BasePromotion;
  token: string;
  drawerView: 'cart' | 'voucher' | 'gift' | 'active_offers';
  setDrawerView: React.Dispatch<
    React.SetStateAction<'cart' | 'voucher' | 'gift' | 'active_offers'>
  >;
  maxQty: number;
  onSaveGift: (data: BaseFreeGift[]) => void;
  colorScheme?: ThemeColorSchemeType;
  initialSelectedGifts?: BaseFreeGift[];
};

export default function FreeGiftList({
  drawerView,
  token,
  promotion,
  setDrawerView,
  maxQty,
  onSaveGift,
  colorScheme,
  initialSelectedGifts = [],
}: FreeGiftListProps) {
  const { ref, inView } = useInView();
  const [selectedTab, setSelectedTab] = useState<'product_group' | 'product_item'>('product_group');
  const [selectedProducts, setSelectedProducts] = useState<BaseFreeGift[]>([]);
  const [selectedProduct, setSelectedProduct] = useState<BasePromotionRewardProduct | undefined>();

  const [paramsProductGroup, setParamsProductGroup] = useState({
    page: 1,
    pageSize: 10,
    q: '',
  });
  const [paramsProductItem, setParamsProductItem] = useState({
    page: 1,
    pageSize: 10,
    q: '',
  });

  const debouncedSearchProduct = useDebounceValue(paramsProductGroup.q, 500);
  const debounceSearchItem = useDebounceValue(paramsProductItem.q, 500);

  const {
    data: dataRewardProduct,
    fetchNextPage,
    hasNextPage,
    isLoading,
    isFetchingNextPage,
  } = useGetRewardProductInfinite(
    {
      promotion_id: promotion?.promotion_id as string,
      promotion_tier_id: promotion.promotion_tier_id as string,
      token,
    },
    {
      pageSize: paramsProductGroup.pageSize,
      q: debouncedSearchProduct,
    },
    {
      enabled: !!promotion.promotion_id && drawerView === 'gift',
      initialPageParam: 1,
    }
  );

  const {
    data: dataRewardItem,
    fetchNextPage: fetchNextPageItem,
    hasNextPage: hasNextPageItem,
    isLoading: isLoadingItem,
    isFetchingNextPage: isFetchingNextPageItem,
  } = useGetRewardProductItemInfinite(
    {
      promotion_id: promotion?.promotion_id as string,
      product_id: selectedProduct?.product_group_id || '',
      promotion_tier_id: promotion.promotion_tier_id as string,
      token,
    },
    {
      page: paramsProductItem.page,
      pageSize: paramsProductItem.pageSize,
      q: debounceSearchItem,
    },
    {
      enabled: !!selectedProduct?.product_group_id && selectedTab === 'product_item',
      initialPageParam: 1,
    }
  );

  const listRewardProduct = dataRewardProduct?.pages.flatMap((page) => page.data) || [];
  const listRewardItem = dataRewardItem?.pages.flatMap((page) => page.data) || [];

  const handleSearchProduct = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setParamsProductGroup((prev) => ({
      ...prev,
      q: e.target.value,
      page: 1,
    }));
  }, []);

  const handleSearchItem = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setParamsProductItem((prev) => ({
      ...prev,
      q: e.target.value,
      page: 1,
    }));
  }, []);

  const totalQty = selectedProducts.reduce(
    (sum, group) => sum + group.items.reduce((gSum, item) => gSum + (item.qty ?? 0), 0),
    0
  );

  const handleUpdateQty = (
    productGroup: BasePromotionRewardProduct,
    item: BasePromotionRewardProductItem,
    delta: number
  ) => {
    setSelectedProducts((prev) => {
      const updated = prev.map((group) => {
        if (group.product_group.product_group_id !== productGroup.product_group_id) return group;

        const itemExists = group.items.find((i) => i.item_id === item.item_id);
        let updatedItems: typeof group.items;

        if (itemExists) {
          const newQty = (itemExists.qty ?? 0) + delta;
          if (newQty <= 0) {
            updatedItems = group.items.filter((i) => i.item_id !== item.item_id);
          } else {
            updatedItems = group.items.map((i) =>
              i.item_id === item.item_id ? { ...i, qty: newQty } : i
            );
          }
        } else {
          updatedItems =
            delta > 0
              ? [
                  ...group.items,
                  {
                    ...item,
                    qty: delta,
                  },
                ]
              : group.items;
        }

        return updatedItems.length > 0 ? { ...group, items: updatedItems } : null;
      });

      const cleaned = updated.filter((g): g is BaseFreeGift => g !== null);

      const groupExists = prev.some(
        (g) => g.product_group.product_group_id === productGroup.product_group_id
      );
      if (!groupExists && delta > 0) {
        cleaned.push({
          product_group: productGroup,
          items: [
            {
              ...item,
              qty: delta,
            },
          ],
        });
      }

      return cleaned;
    });
  };

  useEffect(() => {
    if (hasNextPage && inView) {
      fetchNextPage();
    }
  }, [inView, hasNextPage]);

  useEffect(() => {
    if (hasNextPageItem && inView) {
      fetchNextPageItem();
    }
  }, [inView, hasNextPageItem]);

  useEffect(() => {
    if (drawerView !== 'gift') {
      setSelectedProducts([]);
      setParamsProductGroup({ page: 1, pageSize: 10, q: '' });
    }
  }, [drawerView]);

  useEffect(() => {
    if (selectedTab !== 'product_group') {
      setParamsProductItem({ page: 1, pageSize: 10, q: '' });
    }
  }, [selectedTab]);

  useEffect(() => {
    if (drawerView === 'gift' && initialSelectedGifts && selectedProducts.length === 0) {
      setSelectedProducts(initialSelectedGifts);
    }
  }, [drawerView, initialSelectedGifts, selectedProducts.length]);

  return (
    <>
      <DrawerHeader className='flex items-center justify-between text-left'>
        <div className='flex items-center'>
          <Button
            variant={null}
            onClick={() => {
              if (drawerView === 'gift') {
                const isProductItemTab = selectedTab === 'product_item';
                if (isProductItemTab) {
                  setSelectedTab('product_group');
                } else {
                  setDrawerView('cart');
                }
              } else {
                setDrawerView('gift');
              }
            }}
            size='icon'
          >
            <ArrowLeft className='theme_cart_drawer_color h-[18px] w-[18px]' />
          </Button>
          <DrawerTitle className='theme_cart_drawer_color'>
            Choose your free gift ({totalQty}/{maxQty})
          </DrawerTitle>
        </div>
        <DrawerClose onClick={() => setDrawerView('cart')}>
          <XIcon className='w-4 h-4' />
        </DrawerClose>
      </DrawerHeader>

      {selectedTab === 'product_group' ? (
        <>
          <div className='px-4'>
            <div className='flex items-center gap-2'>
              <input
                type='text'
                placeholder='Search product...'
                value={paramsProductGroup.q}
                onChange={handleSearchProduct}
                className='w-full flex-1 rounded border px-3 py-2.5 text-xs outline-none ring-gray-200 focus:ring-1 focus:ring-[var(--ring-active-color)]'
                style={
                  {
                    '--ring-active-color': colorScheme?.background || '#000000',
                  } as React.CSSProperties
                }
              />
            </div>
          </div>

          <ScrollArea className='h-full px-4 pt-4 overflow-y-auto' type='scroll'>
            <div className='flex flex-col gap-2'>
              {isLoading ? (
                <div className='space-y-1 overflow-y-auto'>
                  {Array.from({ length: 5 }).map((_, idx) => (
                    <Skeleton className='w-full h-10' key={idx} />
                  ))}
                </div>
              ) : listRewardProduct && listRewardProduct?.length > 0 ? (
                <>
                  {listRewardProduct?.map((product) => (
                    <CardGiftProduct
                      key={product.product_group_id}
                      product={product}
                      selectedProducts={selectedProducts}
                      setSelectedTab={setSelectedTab}
                      setSelectedProduct={setSelectedProduct}
                      colorScheme={colorScheme}
                    />
                  ))}
                  <div ref={hasNextPage ? ref : null}></div>
                  {hasNextPage && isFetchingNextPage && (
                    <div className='flex justify-center w-full py-4'>
                      <div className='flex'>
                        <LoadingAnimationIcon size={4} color={colorScheme?.background} />
                        <span className='-ml-1 text-xs'>Loading more</span>
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <div className='flex items-center justify-center h-full'>
                  <p className='text-sm text-gray-500'>No product found.</p>
                </div>
              )}
            </div>
          </ScrollArea>
        </>
      ) : (
        <>
          <div className='px-4 pt-2'>
            <div className='flex items-center gap-2'>
              <Input
                type='text'
                placeholder='Search variant...'
                className='w-full flex-1 px-3 py-2.5 text-xs focus:!ring-[var(--ring-active-color)]'
                classNameWrapper='w-full'
                value={paramsProductItem.q}
                onChange={handleSearchItem}
                style={
                  {
                    '--ring-active-color': colorScheme?.background || '#000000',
                  } as React.CSSProperties
                }
              />
            </div>
          </div>

          <ScrollArea className='h-full px-4 pt-4 overflow-y-auto' type='scroll'>
            <div className='flex flex-col gap-2'>
              {isLoadingItem ? (
                <div className='space-y-1 overflow-y-auto'>
                  {Array.from({ length: 5 }).map((_, idx) => (
                    <Skeleton className='w-full h-10' key={idx} />
                  ))}
                </div>
              ) : listRewardItem && listRewardItem?.length > 0 ? (
                <>
                  {listRewardItem?.map((product) => (
                    <CardGiftProductItem
                      key={product.item_id}
                      product={product}
                      maxQty={maxQty}
                      selectedProduct={selectedProduct as BasePromotionRewardProduct}
                      selectedProducts={selectedProducts}
                      setSelectedTab={setSelectedTab}
                      handleUpdateQty={handleUpdateQty}
                      totalQty={totalQty}
                      colorScheme={colorScheme}
                    />
                  ))}
                  <div ref={hasNextPageItem ? ref : null}></div>
                  {hasNextPageItem && isFetchingNextPageItem && (
                    <div className='flex justify-center w-full py-4'>
                      <div className='flex'>
                        <LoadingAnimationIcon size={4} color={colorScheme?.background} />
                        <span className='-ml-1 text-xs'>Loading more</span>
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <div className='flex items-center justify-center h-full'>
                  <p className='text-sm text-gray-500'>No product found.</p>
                </div>
              )}
            </div>
          </ScrollArea>
        </>
      )}

      <DrawerFooter className='px-4'>
        <div className='flex flex-col gap-2'>
          <Button
            variant='secondary'
            onClick={() => {
              const selected: BaseFreeGift[] = [];

              selectedProducts.forEach((group) => {
                group.items.forEach((item) => {
                  if (item.qty && item.qty > 0) {
                    selected.push({
                      promotion_id: String(promotion.promotion_id),
                      product_group: group.product_group,
                      items: [{ ...item, qty: item.qty }],
                    });
                  }
                });
              });

              onSaveGift(selected);
              setDrawerView('cart');
            }}
            className='items-center w-full px-24 theme_button theme_cart_drawer_btn underline-offset-2'
            disabled={totalQty === 0}
          >
            Add to cart
          </Button>
        </div>
      </DrawerFooter>
    </>
  );
}
